import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { functionErrorMessage } from '../../lib/functionError';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import { DeleteModal } from '../../components/DeleteModal';
import { deleteWorkspace } from '../../lib/workspaces';
import { metaCookies } from '../../lib/metaPixel';
import { getPlanDisplayName } from '../../lib/plans';
import { BillingToggle, PricingCards, type PricingPlan } from '../../components/PricingCards';

// An Awaiting payment workspace is paid for here, then onboarding carries on at step 2.
export function OnboardingPayment() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user, workspace, viewingAs, fetchProfile } = useAuthStore();
  const [searchParams] = useSearchParams();
  const isConfirming = searchParams.get('paid') === '1';
  const [isYearly, setIsYearly] = useState(false);
  const [busy, setBusy] = useState<null | PricingPlan | 'delete' | 'later'>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: otherWorkspaceId } = useQuery({
    queryKey: ['otherWorkspace', user?.id, workspace?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('workspace_members')
        .select('workspace_id')
        .eq('user_id', user!.id)
        .neq('workspace_id', workspace!.id)
        .limit(1);
      return data?.[0]?.workspace_id ?? null;
    },
    enabled: !!user?.id && !!workspace?.id,
  });

  // Once it's Paid, carry on setting it up.
  useEffect(() => {
    if (workspace?.state && workspace.state !== 'awaiting_payment') {
      navigate('/onboarding/step-2', { replace: true });
    }
  }, [workspace?.state, navigate]);

  // Back from Stripe: the webhook marks the workspace Paid a few seconds later.
  useEffect(() => {
    if (!isConfirming || !user) return;
    const timer = setInterval(() => fetchProfile(user.id), 2000);
    return () => clearInterval(timer);
  }, [isConfirming, user, fetchProfile]);

  const handleCheckout = async (plan: PricingPlan) => {
    if (!user || !workspace) return;

    setBusy(plan);
    try {
      // Paying sets the workspace up, so onboarding picks up after checkout.
      await supabase.from('profiles').update({ onboarding_completed: false }).eq('id', user.id);

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          plan,
          billingInterval: isYearly ? 'yearly' : 'monthly',
          workspaceId: workspace.id,
          successUrl: `${window.location.origin}/onboarding/payment?paid=1`,
          cancelUrl: `${window.location.origin}/onboarding/payment`,
          ...metaCookies(),
        },
      });

      if (error) throw new Error(await functionErrorMessage(error));
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (error: any) {
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setBusy(null);
    }
  };

  const handlePayLater = async () => {
    if (!user || !otherWorkspaceId) return;
    setBusy('later');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ workspace_id: otherWorkspaceId, onboarding_completed: true })
        .eq('id', user.id);
      if (error) throw error;
      await fetchProfile(user.id);
      queryClient.invalidateQueries();
      navigate('/overview', { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Failed to switch workspace');
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!user || !workspace) return;
    setBusy('delete');
    try {
      const nextWorkspace = await deleteWorkspace(user.id, workspace.id);
      toast.success('Workspace deleted');
      await fetchProfile(user.id);
      queryClient.invalidateQueries();
      navigate(nextWorkspace?.id ? '/overview' : '/onboarding/step-1', { replace: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete workspace');
      setBusy(null);
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        {/* The plan cards are taller than the screen, so start them below a padded top instead of centring */}
        <div className={`onboarding-wrapper ${isConfirming ? '' : 'padding-global'}`}>
          <div className="intro-wrapper">
            {isConfirming ? (
              <>
                <div className="loading-spinner" />
                <div className="spacer-small"></div>
                <div className="text-size-large text-weight-semibold text-colour-dark-brown">
                  Confirming your payment…
                </div>
                <div className="spacer-xxtiny"></div>
                <div className="text-size-regular text-colour-alt-grey align-center">
                  This only takes a few seconds. We'll start setting up {workspace?.name || 'your workspace'} next.
                </div>
              </>
            ) : (
              <>
                <div className="text-size-large text-weight-semibold text-colour-dark-brown">
                  Choose a plan for {workspace?.name || 'this workspace'}
                </div>
                <div className="spacer-xxtiny"></div>
                <div className="text-size-regular text-colour-alt-grey align-center">
                  You've used your free Trial on a previous Workspace, so {workspace?.brand_url || 'this brand'} needs a Paid Subscription before we start tracking it.
                </div>
                <div className="spacer-medium"></div>
                <div className="align-content-middle">
                  <BillingToggle isYearly={isYearly} onChange={setIsYearly} />
                </div>
                <div className="spacer-medium"></div>
                <div className="container-medium" style={{ width: '100%', textAlign: 'left' }}>
                  <PricingCards
                    isYearly={isYearly}
                    renderButton={(plan) => viewingAs ? null : (
                      <button
                        type="button"
                        className="button is-wide"
                        disabled={busy !== null}
                        onClick={() => handleCheckout(plan)}
                      >
                        {busy === plan ? 'Loading...' : `Choose ${getPlanDisplayName(plan)}`}
                      </button>
                    )}
                  />
                </div>
                <div className="spacer-small"></div>
                <div className="align-content-middle" style={{ gap: '0.5rem' }}>
                  {otherWorkspaceId && (
                    <button type="button" className="link-style" onClick={handlePayLater} disabled={busy !== null}>
                      Switch Workspace and Pay Later
                    </button>
                  )}
                  {!viewingAs && (
                    <button type="button" className="link-style" onClick={() => setShowDeleteModal(true)} disabled={busy !== null}>
                      Delete workspace
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="gradient-style"></div>
      </div>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete workspace"
        message="This workspace hasn't been paid for yet. Deleting it can't be undone."
        confirmText="Delete"
        loading={busy === 'delete'}
      />
    </div>
  );
}
